import { useState, useEffect, useCallback } from 'react'
import { widgetRegistry, DEFAULT_LAYOUT } from '../data/widgetRegistry'
interface WidgetLayoutState {
  widgets: string[]
  seenWidgets: string[]
  firstTimeDismissed: boolean
}

// Changed key to force fresh layout for all users (old key had corrupted layouts)
const STORAGE_KEY = 'vsm_dashboard_v3'
// Minimum widgets to consider a layout valid (prevents partial/corrupted saves)
const MIN_WIDGETS = Math.ceil(DEFAULT_LAYOUT.length / 2)
export function useWidgetLayout() {
  const [state, setState] = useState<WidgetLayoutState>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        const validIds = widgetRegistry.map((w) => w.id)
        const validWidgets = (parsed.widgets || []).filter((id: string) =>
          validIds.includes(id),
        )

        // Reset if layout has too few valid widgets (corrupted or partial save)
        if (validWidgets.length < MIN_WIDGETS) {
          return {
            widgets: DEFAULT_LAYOUT,
            seenWidgets: parsed.seenWidgets || [],
            firstTimeDismissed: parsed.firstTimeDismissed || false,
          }
        }
        return {
          ...parsed,
          widgets: validWidgets,
        }
      }
    } catch (e) {
      console.error('Failed to parse widget layout from localStorage', e)
    }
    return {
      widgets: DEFAULT_LAYOUT,
      seenWidgets: [],
      firstTimeDismissed: false,
    }
  })
  const [editMode, setEditMode] = useState(false)

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }, [state])
  const addWidget = useCallback((id: string) => {
    setState((prev) => {
      if (prev.widgets.includes(id)) return prev
      return {
        ...prev,
        widgets: [...prev.widgets, id],
      }
    })
  }, [])
  const removeWidget = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      widgets: prev.widgets.filter((w) => w !== id),
    }))
  }, [])
  const moveWidget = useCallback((fromIndex: number, toIndex: number) => {
    setState((prev) => {
      const newWidgets = [...prev.widgets]
      const [movedItem] = newWidgets.splice(fromIndex, 1)
      newWidgets.splice(toIndex, 0, movedItem)
      return {
        ...prev,
        widgets: newWidgets,
      }
    })
  }, [])
  const resetToDefault = useCallback(() => {
    setState((prev) => ({
      ...prev,
      widgets: DEFAULT_LAYOUT,
    }))
  }, [])
  const dismissFirstTime = useCallback(() => {
    setState((prev) => ({
      ...prev,
      firstTimeDismissed: true,
    }))
  }, [])
  const markAllSeen = useCallback(() => {
    setState((prev) => ({
      ...prev,
      seenWidgets: widgetRegistry.map((w) => w.id),
    }))
  }, [])
  const newWidgetCount = widgetRegistry.filter(
    (w) => w.isNew && !state.seenWidgets.includes(w.id),
  ).length
  return {
    layout: state.widgets,
    isFirstTime: !state.firstTimeDismissed,
    hasNewWidgets: newWidgetCount > 0,
    newWidgetCount,
    editMode,
    setEditMode,
    addWidget,
    removeWidget,
    moveWidget,
    resetToDefault,
    dismissFirstTime,
    markAllSeen,
  }
}
